/**
 * Elements lies (« Related items » de 1Password, « Linked records » de Keeper) : une entree pointe vers les autres
 * entrees qui vont avec elle -- le compte de messagerie qui recoit les reinitialisations d'un site, la carte bancaire
 * enregistree chez un marchand, la cle SSH d'un serveur, le routeur d'un reseau Wi-Fi. Depuis la fiche, l'element
 * lie s'ouvre en un clic, ou qu'il soit range (dossier, archives).
 *
 * Le lien vit DANS les secrets chiffres de l'entree qui le pose (`secrets.links` : identifiants d'entrees), comme
 * `archived` : le serveur ne voit pas quelles entrees vont ensemble, il ne voit qu'une revision qui avance. Le lien se
 * LIT dans les deux sens -- la messagerie montre aussi le site qui la designe -- sans reecrire l'autre entree. Un lien
 * vers une entree jetee ou supprimee se tait (une entree restauree le retrouve) ; un identifiant illisible est ignore.
 *
 * Une fusion de doublons reporte les liens des copies jetees, et ceux qui les designaient, sur la copie gardee.
 * Un import reprend les liens de l'export (champ `reference` de 1Password, champ `klavox-links` du CXF de Klavox) : ils
 * designent des identifiants de l'export, resolus ici vers les entrees creees.
 *
 * Module pur, zero E/S : `VaultStore.linkEntries` / `unlinkEntries` / `mergeEntries` et l'import le composent.
 */
import type { EntrySecrets } from '@klavox/crypto-core';
import type { VaultItem } from '@klavox/vault-model';
import type { ImportedEntry } from './csv-import.js';
import { isTrashed } from './entry-trash.js';

/** Liens gardes par entree, au-dela ignores (un import forge ne fait pas grossir une fiche sans fin). */
export const MAX_ENTRY_LINKS = 50;

/** Identifiants lies par l'entree elle-meme : chaines non vides, sans doublon, bornees ; le reste est ignore. */
export function readLinks(secrets: EntrySecrets): string[] {
  const raw = secrets['links'];
  if (!Array.isArray(raw)) return [];
  const ids: string[] = [];
  for (const value of raw) {
    if (typeof value !== 'string' || value.length === 0 || ids.includes(value)) continue;
    ids.push(value);
    if (ids.length === MAX_ENTRY_LINKS) break;
  }
  return ids;
}

/** Secrets avec un lien de plus vers `otherId` ; inchanges s'il y est deja ou si la limite est atteinte. */
export function withLink(secrets: EntrySecrets, otherId: string): EntrySecrets {
  const links = readLinks(secrets);
  if (links.includes(otherId) || links.length >= MAX_ENTRY_LINKS) return secrets;
  return { ...secrets, links: [...links, otherId] };
}

/** Secrets sans le lien vers `otherId` ; la cle disparait avec le dernier lien. */
export function withoutLink(secrets: EntrySecrets, otherId: string): EntrySecrets {
  return withLinks(secrets, readLinks(secrets).filter((id) => id !== otherId));
}

/** Secrets dont la liste de liens est `ids` (bornee, sans doublon) ; la cle disparait si elle est vide. */
function withLinks(secrets: EntrySecrets, ids: readonly string[]): EntrySecrets {
  const { links: _links, ...rest } = secrets;
  const kept = readLinks({ links: [...ids] });
  return kept.length > 0 ? { ...rest, links: kept } : rest;
}

/**
 * Fusion de doublons (`VaultStore.mergeEntries`) : les liens de la copie gardee, puis ceux des copies jetees, sans
 * doublon -- jamais vers la copie gardee elle-meme ni vers une copie jetee. Sans cela, ceux d'une copie se perdaient
 * (ou devenaient un champ masque fait d'identifiants).
 */
export function withMergedLinks(secrets: EntrySecrets, keepId: string, dropped: readonly VaultItem[]): EntrySecrets {
  const droppedIds = new Set(dropped.map((item) => item.id));
  const ids = [secrets, ...dropped.map((item) => item.secrets)].flatMap(readLinks);
  return withLinks(secrets, ids.filter((id) => id !== keepId && !droppedIds.has(id)));
}

/**
 * Fusion de doublons, l'autre sens : une entree qui designait une copie jetee designe desormais la copie gardee.
 * Renvoie les MEMES secrets (meme reference) si l'entree ne designait aucune copie : rien a reecrire.
 */
export function withRetargetedLinks(secrets: EntrySecrets, droppedIds: ReadonlySet<string>, keepId: string): EntrySecrets {
  const links = readLinks(secrets);
  if (!links.some((id) => droppedIds.has(id))) return secrets;
  return withLinks(secrets, links.map((id) => (droppedIds.has(id) ? keepId : id)));
}

/** Entree encore vivante (ni supprimee, ni a la corbeille) : seule elle peut etre liee ou montree comme liee. */
function isLinkable(item: VaultItem): boolean {
  return item.deletedAt === null && !isTrashed(item.secrets);
}

function byName(a: VaultItem, b: VaultItem): number {
  return (a.secrets.name ?? '').localeCompare(b.secrets.name ?? '', 'fr', { sensitivity: 'base' }) || a.id.localeCompare(b.id);
}

/**
 * Elements lies a `entry`, dans les deux sens : ceux qu'elle designe et ceux qui la designent, une seule fois chacun,
 * hors elle-meme et hors corbeille, tries par nom. `all` = le coffre entier, archives comprises.
 */
export function relatedEntries(entry: VaultItem, all: readonly VaultItem[]): VaultItem[] {
  const own = new Set(readLinks(entry.secrets));
  return all
    .filter((item) => item.id !== entry.id && isLinkable(item) && (own.has(item.id) || readLinks(item.secrets).includes(entry.id)))
    .sort(byName);
}

/** Entrees que l'on peut encore lier a `entry` : vivantes, ni elle-meme, ni deja liees (dans un sens ou l'autre). */
export function linkCandidates(entry: VaultItem, all: readonly VaultItem[]): VaultItem[] {
  const related = new Set(relatedEntries(entry, all).map((item) => item.id));
  return all.filter((item) => item.id !== entry.id && isLinkable(item) && !related.has(item.id)).sort(byName);
}

/** Lien a poser apres un import : `from` designe `to`, deux identifiants d'entrees du coffre. */
export interface ImportedLink {
  readonly from: string;
  readonly to: string;
}

/**
 * Liens d'un import, resolus : `ids[i]` = entree du coffre qui a recu `entries[i]` (creee, ou fiche existante qui a
 * pris sa graine 2FA), `null` si rien. Un `sourceId` vu deux fois garde sa premiere entree (export forge) ; un lien
 * vers soi-meme ou deja pose (dans un sens ou l'autre) est ignore ; un lien vers un element ABSENT de l'export (mis a la corbeille, coffre non
 * exporte) est COMPTE dans `missing` -- l'import le dit, il ne le perd pas en silence. Au-dela de `MAX_ENTRY_LINKS`
 * par entree, les liens suivants sont ignores, comme a la lecture.
 */
export function resolveImportedLinks(entries: readonly ImportedEntry[], ids: readonly (string | null)[]): { links: ImportedLink[]; missing: number } {
  const bySource = new Map<string, string>();
  entries.forEach((entry, index) => {
    const id = ids[index];
    if (entry.sourceId !== undefined && entry.sourceId.length > 0 && id != null && !bySource.has(entry.sourceId)) bySource.set(entry.sourceId, id);
  });
  const links: ImportedLink[] = [];
  const seen = new Set<string>();
  const perEntry = new Map<string, number>();
  let missing = 0;
  entries.forEach((entry, index) => {
    const from = ids[index];
    if (from == null) return;
    for (const source of entry.linkedSourceIds ?? []) {
      const to = bySource.get(source);
      if (to === undefined) {
        missing += 1;
        continue;
      }
      const key = JSON.stringify([from, to]);
      // Un lien se lit dans les deux sens : B -> A deja pose rend A -> B inutile (1Password les pose souvent des deux cotes).
      if (to === from || seen.has(key) || seen.has(JSON.stringify([to, from])) || (perEntry.get(from) ?? 0) >= MAX_ENTRY_LINKS) continue;
      seen.add(key);
      perEntry.set(from, (perEntry.get(from) ?? 0) + 1);
      links.push({ from, to });
    }
  });
  return { links, missing };
}
